import React from "react";
import { motion } from "framer-motion";
import { ShieldStar, Sparkle, Compass } from "@phosphor-icons/react";

const FOUNDING_COIN_URL =
    "https://customer-assets-gfyr7b9c.emergentagent.net/job_battery-legacy/artifacts/rwl8znkf_CoinSide1.png";

const TIER_CONFIG = {
    founding_100: {
        label: "Founding Earth Guardian",
        short: "Founder",
        cap: "First 100 Members",
        color: "#a3e635",
        bg: "linear-gradient(135deg, rgba(163,230,53,0.18), rgba(6,182,212,0.12))",
        ringColor: "rgba(163,230,53,0.5)",
        icon: ShieldStar,
        coin: FOUNDING_COIN_URL,
        celebratory: "You joined before the world knew.",
    },
    pioneer_1000: {
        label: "Earth Pioneer",
        short: "Pioneer",
        cap: "First 1,000 Members",
        color: "#06b6d4",
        bg: "linear-gradient(135deg, rgba(6,182,212,0.18), rgba(139,92,246,0.12))",
        ringColor: "rgba(6,182,212,0.5)",
        icon: Compass,
        celebratory: "You showed up when it mattered most.",
    },
    member: {
        label: "Earth Member",
        short: "Member",
        cap: "EVIQ Community",
        color: "#a1a1aa",
        bg: "linear-gradient(135deg, rgba(161,161,170,0.14), rgba(63,63,70,0.14))",
        ringColor: "rgba(161,161,170,0.4)",
        icon: Sparkle,
        celebratory: "Welcome to the movement.",
    },
};

export const getTierConfig = (tier) => TIER_CONFIG[tier] || TIER_CONFIG.member;

const formatMemberNumber = (n) => `#${String(n).padStart(4, "0")}`;

/**
 * FoundingBadge — three sizes:
 *   size="sm" → nav / inline chip
 *   size="md" → hero corner
 *   size="lg" → dedicated card / welcome modal
 */
export const FoundingBadge = ({ tier = "member", memberNumber = 0, size = "md", testId }) => {
    const cfg = getTierConfig(tier);
    const Icon = cfg.icon;

    if (size === "sm") {
        return (
            <span
                data-testid={testId}
                className="inline-flex items-center gap-2 rounded-full border px-3 py-1 font-mono-metric text-[10px] uppercase tracking-[0.18em]"
                style={{ borderColor: cfg.ringColor, background: cfg.bg, color: cfg.color }}
            >
                <Icon size={12} weight="fill" />
                {cfg.short} {formatMemberNumber(memberNumber)}
            </span>
        );
    }

    if (size === "lg") {
        // Founding tier gets the coin artwork; other tiers keep the SVG medallion.
        if (cfg.coin) {
            // Prefer the personalized coin PNG (member number burned into the
            // baked "#0000" cartouche) so downloaded/printed keepsakes show
            // the true number. Falls back to the raw asset on any load error.
            const backend = process.env.REACT_APP_BACKEND_URL || "";
            const personalizedUrl =
                backend && memberNumber
                    ? `${backend}/api/coin/founder/${memberNumber}.png`
                    : cfg.coin;
            return (
                <motion.div
                    data-testid={testId}
                    initial={{ scale: 0.85, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                    className="relative mx-auto flex flex-col items-center gap-3"
                >
                    <div className="relative h-48 w-48">
                        <div
                            className="absolute inset-0 rounded-full blur-2xl"
                            style={{ background: cfg.color, opacity: 0.28 }}
                        />
                        <div
                            className="absolute inset-0 rounded-full"
                            style={{
                                background:
                                    "radial-gradient(circle at 30% 25%, rgba(255,255,255,0.10), rgba(0,0,0,0) 60%)",
                            }}
                        />
                        <img
                            src={personalizedUrl}
                            alt={cfg.label}
                            onError={(e) => {
                                if (e.currentTarget.src !== cfg.coin) {
                                    e.currentTarget.src = cfg.coin;
                                }
                            }}
                            className="relative z-10 h-48 w-48 rounded-full object-contain drop-shadow-[0_10px_30px_rgba(163,230,53,0.35)]"
                            loading="eager"
                        />
                    </div>
                    <span className="font-mono-metric text-[10px] uppercase tracking-[0.24em] text-zinc-500">
                        {cfg.cap}
                    </span>
                </motion.div>
            );
        }
        return (
            <motion.div
                data-testid={testId}
                initial={{ scale: 0.85, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                className="relative mx-auto flex h-40 w-40 items-center justify-center"
            >
                <div
                    className="absolute inset-0 rounded-full blur-2xl"
                    style={{ background: cfg.color, opacity: 0.25 }}
                />
                <div
                    className="relative flex h-40 w-40 items-center justify-center rounded-full border-2"
                    style={{ borderColor: cfg.ringColor, background: cfg.bg }}
                >
                    <div
                        className="flex h-32 w-32 flex-col items-center justify-center rounded-full border"
                        style={{ borderColor: cfg.ringColor, background: "#050505" }}
                    >
                        <Icon size={30} color={cfg.color} weight="fill" />
                        <span
                            className="mt-2 font-mono-metric text-2xl font-bold"
                            style={{ color: cfg.color }}
                        >
                            {formatMemberNumber(memberNumber)}
                        </span>
                        <span className="mt-1 font-mono-metric text-[9px] uppercase tracking-[0.24em] text-zinc-500">
                            {cfg.cap}
                        </span>
                    </div>
                </div>
            </motion.div>
        );
    }

    // md — default
    return (
        <div
            data-testid={testId}
            className="inline-flex items-center gap-3 rounded-lg border px-4 py-3"
            style={{ borderColor: cfg.ringColor, background: cfg.bg }}
        >
            <div
                className="flex h-10 w-10 items-center justify-center rounded-md border"
                style={{ borderColor: cfg.ringColor, background: "#050505" }}
            >
                <Icon size={18} color={cfg.color} weight="fill" />
            </div>
            <div className="flex flex-col leading-tight">
                <span
                    className="font-mono-metric text-[10px] uppercase tracking-[0.24em]"
                    style={{ color: cfg.color }}
                >
                    {cfg.cap}
                </span>
                <span className="font-editorial text-lg text-white">{cfg.label}</span>
                <span className="font-mono-metric text-xs text-zinc-500">
                    Member {formatMemberNumber(memberNumber)}
                </span>
            </div>
        </div>
    );
};

export default FoundingBadge;
